import React, { FC, useEffect, useState, Fragment, useRef } from 'react';
import Validationtip from '../validationtip/Validationtip';
import { BaseModel } from '../../models/BaseModel';
import InputFieldText from '../input/floating-label/input-field/InputFieldText';
import { START_WITH } from '../../enum/FilterTypen';
import {
  ARROW_DOWN,
  ARROW_UP,
  BACKSPACE,
  ENTER,
  ESCAPE,
  TAB,
} from '../../utils/constants';

type SelectProps = {
  headerTitle?: string;
  name?: string;
  className?: string;
  selectedOptionId?: number; //ID des Objekts
  onChangeHandler?: (id?: number) => void; //ID des Objekts
  onBlurHandler?: () => void;
  selectData: SelectOption[];
  selectionDenied?: boolean;
  activeNotificationComponent?: string; //Dient zur Anzeige von Tip in einem bestimmten Bereich
  readonly?: boolean;
  filterList?: boolean;
  filterType?: string;
};

export interface SelectOption extends BaseModel {
  elementId: number; //ID für die GUI2
  id?: number; //ID des Objekts
  value: string;
  className?: string;
}

const Select: FC<SelectProps> = (props: SelectProps) => {
  const [isListOpen, setIsListOpen] = useState(false);
  const [selectedOptionTitle, setSelectedOptionTitle] = useState(
    props.selectData?.find(item => item.id == props.selectedOptionId)?.value ??
      ''
  );
  const [filteredList, setFilteredList] = useState<SelectOption[]>(
    props.selectData
  );
  const [focusSelectId, setFocusSelectId] = useState<number | undefined>(-1);
  const focusedLiRef = useRef<HTMLLIElement>(null);
  const inputRef = React.useRef() as React.MutableRefObject<HTMLInputElement>;

  const optionCount = () => {
    return props.selectData.length;
  };

  const focusInputField = () => {
    inputRef?.current?.focus();
  };

  const closeList = () => {
    setIsListOpen(false);
  };

  const updateOptionTitle = () => {
    if (props.selectedOptionId !== undefined) {
      const option = props.selectData.find(
        option => option.id == props.selectedOptionId
      );
      if (option && option.value !== selectedOptionTitle) {
        setSelectedOptionTitle(option.value);
      }
    } else {
      setSelectedOptionTitle('');
    }
  };

  useEffect(() => {
    updateOptionTitle();
  }, [props.selectedOptionId, props.selectData]);

  useEffect(() => {
    focusedLiRef?.current?.focus();
  }, [focusSelectId]);

  useEffect(() => {
    if (!isListOpen) return;
    window.addEventListener('click', closeList);
    focusInputField();
    setFocusSelectId(-1);
    return () => window.removeEventListener('click', closeList);
  }, [isListOpen]);

  useEffect(() => {
    if (props.filterList && selectedOptionTitle && selectedOptionTitle !== '') {
      const selectOptions = filterSelectData();
      setFilteredList(selectOptions);
    }
  }, [selectedOptionTitle]);

  const toggleList = () => {
    if (!props.readonly) {
      if (props.selectionDenied) return;
      setIsListOpen(!isListOpen);
      setFilteredList(props.selectData);
      focusedLiRef.current?.focus();
    }
  };

  const setOption = (option: SelectOption) => {
    setSelectedOptionTitle(option.value);
    !!props.onChangeHandler && props.onChangeHandler(option.id);
    !!props.onBlurHandler && props.onBlurHandler();
    toggleList();
  };

  const isCurrentOptionSelected = (option: SelectOption) => {
    return props.selectedOptionId === option.id;
  };

  const handleKeyDown = (e: React.KeyboardEvent, option: SelectOption) => {
    if (e.key === ENTER) {
      setOption(option);
    } else if (e.key === ARROW_UP || (e.key === TAB && e.shiftKey)) {
      e.preventDefault();
      focusPreviousItem();
    } else if (e.key === ARROW_DOWN || (e.key === TAB && !e.shiftKey)) {
      e.preventDefault();
      focusNextItem();
    } else if (e.key === ESCAPE) {
      setIsListOpen(false);
    }
  };

  const isLastChild = (index: number) => {
    return index + 1 === optionCount();
  };

  const filterSelectData = () => {
    return props.filterType === START_WITH
      ? props.selectData.filter(
          option =>
            selectedOptionTitle === '' ||
            option.value
              .toLowerCase()
              .startsWith(selectedOptionTitle?.toLowerCase())
        )
      : props.selectData.filter(
          option =>
            selectedOptionTitle === '' ||
            option.value
              .toLowerCase()
              .includes(selectedOptionTitle?.toLowerCase())
        );
  };

  const onChangeFilterInputHandler = (value: string) => {
    if (!value) {
      props.onChangeHandler && props.onChangeHandler(undefined);
      setSelectedOptionTitle('');
      setFilteredList(props.selectData);
    } else {
      props.filterList && setFilteredList(filterSelectData());
      setIsListOpen(true);
      setSelectedOptionTitle(value);
    }
  };

  const onKeyDownHandler = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case ENTER:
        toggleList();
        break;
      case BACKSPACE:
        if (props.selectedOptionId && selectedOptionTitle) {
          setSelectedOptionTitle('');
          onChangeFilterInputHandler('');
        }
        break;
      case ESCAPE:
        setIsListOpen(false);
        break;
      case ARROW_DOWN:
      case TAB:
        e.preventDefault();
        !isListOpen && setIsListOpen(true);
        focusNextItem();
        break;
    }
  };

  const focusNextItem = () => {
    if (focusSelectId !== undefined) {
      if (
        filteredList.indexOf(
          filteredList.filter(item => item.elementId === focusSelectId)[0]
        ) +
          1 !==
        filteredList.length
      ) {
        const elementId =
          filteredList[
            filteredList.indexOf(
              filteredList.filter(item => item.elementId === focusSelectId)[0]
            ) + 1
          ]?.elementId;
        setFocusSelectId(elementId);
      }
    }
  };

  const focusPreviousItem = () => {
    //Die ursprüngliche Bedingung war focusSelectId && focusSelectId !== 0, da ist der Fall mit 0 dann durch die implizite Typkonvertierung in der ersten Bedingung inklusive
    if (!!focusSelectId) {
      if (
        filteredList.indexOf(
          filteredList.filter(item => item.elementId === focusSelectId)[0]
        ) -
          1 >=
        0
      ) {
        setFocusSelectId(
          filteredList[
            filteredList.indexOf(
              filteredList.filter(item => item.elementId === focusSelectId)[0]
            ) - 1
          ]?.elementId
        );
      }
    }
  };

  const onBlurHandler = () => {
    if (!props.selectedOptionId) {
      setSelectedOptionTitle('');
      // setFocusSelectId(-1);
    }
  };

  return (
    <div
      className={`form-group select-wrapper ${props.className ?? ''}`}
      onKeyDown={e => !props.filterList && onKeyDownHandler(e)}
      onBlur={!props.filterList ? onBlurHandler : undefined}
    >
      <Validationtip
        fieldName={props.name ?? ''}
        activeNotificationComponent={props.activeNotificationComponent}
      >
        <div
          className='select-header'
          onClick={() => {
            toggleList();
          }}
          tabIndex={0}
          aria-readonly={props.readonly}
        >
          {props.filterList ? (
            <Fragment>
              <InputFieldText
                onChangeHandler={onChangeFilterInputHandler}
                value={selectedOptionTitle}
                label={props.headerTitle}
                readonly={props.readonly}
                onKeyDownHandler={e => onKeyDownHandler(e)}
                onBlurHandler={onBlurHandler}
                ref={inputRef}
              />
              {isListOpen ? (
                <i className='select-arrow fas fa-angle-up' />
              ) : (
                <i className='select-arrow fas fa-angle-down' />
              )}
            </Fragment>
          ) : (
            <Fragment>
              {selectedOptionTitle !== '' || isListOpen ? (
                <label className={'select-label-floating'}>
                  {props.headerTitle ?? ''}
                </label>
              ) : (
                <label className={'select-label'}>
                  {props.headerTitle ?? ''}
                </label>
              )}
              {/* Ausgewaehltes Element */}
              <div
                className={`select-header-title ${
                  !!selectedOptionTitle && 'selected'
                }`}
              >
                {props.selectedOptionId !== undefined
                  ? `${selectedOptionTitle}`
                  : ''}
              </div>

              {isListOpen ? (
                <i className='select-arrow fas fa-angle-up' />
              ) : (
                <i className='select-arrow fas fa-angle-down' />
              )}
            </Fragment>
          )}
        </div>
        {isListOpen && (
          <div className={`select-list`}>
            {/* durch tabIndex= -1 wird das Element nicht mehr per tab gefocussed */}
            <ul tabIndex={-1}>
              {filteredList?.map((option, index) => (
                <li
                  key={index}
                  ref={
                    focusSelectId === option.elementId
                      ? focusedLiRef
                      : undefined
                  }
                  className={`select-list-option ${option.className ?? ''} ${
                    isCurrentOptionSelected(option) && 'bold'
                  }`}
                  onClick={() => {
                    setOption(option);
                  }}
                  onKeyDown={e => {
                    handleKeyDown(e, option);
                  }}
                  tabIndex={0}
                >
                  {option.value}
                </li>
              ))}
            </ul>
          </div>
        )}
      </Validationtip>
    </div>
  );
};

Select.defaultProps = {
  readonly: false,
  filterList: true,
  filterType: START_WITH,
};

export default Select;
